/**
 * Commission V2 Service
 * 
 * Service สำหรับเรียก Commission V2 Cloud Functions
 */

import { callCloudFunction } from '@/services/adapters/parse/parse-client'
import type {
  GetMemberCommissionResponse,
  WithdrawCommissionResponse,
  CheckWithdrawTimeResponse,
  GetCommissionHistoryResponse,
  GetCommissionLogsResponse,
  GetAllCommissionResponse,
  RunCalculationResponse
} from './commission-v2.types'

export class CommissionV2Service {
  /**
   * ดึงข้อมูล Commission ของสมาชิก
   * @param memberId - Member ID (optional, ถ้าไม่ส่งจะใช้ current user)
   * @param sessionToken - Session token
   */
  static async getMemberCommission(
    memberId?: string,
    sessionToken?: string
  ): Promise<GetMemberCommissionResponse> {
    return callCloudFunction(
      'getMemberCommissionV2',
      { member_id: memberId },
      sessionToken
    )
  }

  /**
   * ถอน Commission (ถอนทั้งหมด)
   * @param memberId - Member ID
   * @param sessionToken - Session token
   */
  static async withdrawCommission(
    memberId: string,
    sessionToken?: string
  ): Promise<WithdrawCommissionResponse> {
    return callCloudFunction(
      'withdrawCommissionV2',
      { member_id: memberId },
      sessionToken
    )
  }

  /**
   * เช็คว่าถอนได้หรือยัง (ตามเวลาที่กำหนด)
   * @param sessionToken - Session token
   */
  static async checkWithdrawTime(
    sessionToken?: string
  ): Promise<CheckWithdrawTimeResponse> {
    return callCloudFunction(
      'checkCommissionWithdrawTimeV2',
      {},
      sessionToken
    )
  }

  /**
   * ดึงประวัติการถอน Commission
   * @param memberId - Member ID (optional)
   * @param limit - จำนวน records (default: 20)
   * @param skip - Skip records (default: 0)
   * @param sessionToken - Session token
   */
  static async getWithdrawHistory(
    memberId?: string,
    limit: number = 20,
    skip: number = 0,
    sessionToken?: string
  ): Promise<GetCommissionHistoryResponse> {
    return callCloudFunction(
      'getCommissionHistoryV2',
      { member_id: memberId, limit, skip },
      sessionToken
    )
  }

  /**
   * ดึงประวัติการคำนวณ Commission (Admin only)
   * @param memberId - Member ID (optional)
   * @param limit - จำนวน records (default: 20)
   * @param skip - Skip records (default: 0)
   * @param sessionToken - Session token
   */
  static async getCalculationLogs(
    memberId?: string,
    limit: number = 20,
    skip: number = 0,
    sessionToken?: string
  ): Promise<GetCommissionLogsResponse> {
    return callCloudFunction(
      'getCommissionCalculationLogV2',
      { member_id: memberId, limit, skip },
      sessionToken
    )
  }

  /**
   * ดึงรายการ Commission ของสมาชิกทั้งหมด (Admin only)
   * @param type - 'member' | 'partner' (optional)
   * @param limit - จำนวน records (default: 50)
   * @param skip - Skip records (default: 0)
   * @param sessionToken - Session token
   */
  static async getAllMemberCommission(
    type?: 'member' | 'partner',
    limit: number = 50,
    skip: number = 0,
    sessionToken?: string
  ): Promise<GetAllCommissionResponse> {
    return callCloudFunction(
      'getAllMemberCommissionV2',
      { type, limit, skip },
      sessionToken
    )
  }

  /**
   * รัน Commission Calculation แบบ Manual (Admin only)
   * @param type - 'member' | 'partner' | 'all'
   * @param sessionToken - Session token
   */
  static async runCalculation(
    type: 'member' | 'partner' | 'all' = 'all',
    sessionToken?: string
  ): Promise<RunCalculationResponse> {
    return callCloudFunction(
      'runCommissionV2Calculation',
      { type },
      sessionToken
    )
  }
}
